import { expectRecord, expectString } from "./schema";
import { parseStudentBillingCurrency, parseStudentBillingDate, parseStudentBillingIdentifier, parseStudentBillingServiceMonth } from "./student-billing";
export const STUDENT_BILLING_LESSON_KINDS = ["regular", "shortTerm"];
export const STUDENT_BILLING_FEE_STATUSES = ["active", "ended"];
export const STUDENT_BILLING_EVENT_FEE_ITEM_STATUSES = ["active", "inactive"];
export const STUDENT_BILLING_EVENT_BILLING_MONTH_RULES = ["eventFirstMonth", "explicitServiceMonth"];
export const STUDENT_BILLING_MIGRATION_COMPARISON_STATUSES = ["noCurrentMvpData", "uniform", "mixed"];
function exact(record, allowed, path) {
    const unknown = Object.keys(record).filter((key) => !allowed.includes(key));
    if (unknown.length > 0)
        throw new Error(`${path} contains unknown fields: ${unknown.sort().join(", ")}`);
}
function enumValue(value, allowed, path) {
    const parsed = expectString(value, path);
    if (!allowed.includes(parsed))
        throw new Error(`${path} is invalid`);
    return parsed;
}
function nonNegativeInteger(value, path) {
    if (!Number.isSafeInteger(value) || value < 0)
        throw new Error(`${path} must be a non-negative integer`);
    return value;
}
function positiveInteger(value, path) {
    if (!Number.isSafeInteger(value) || value < 1)
        throw new Error(`${path} must be a positive integer`);
    return value;
}
function boundedLabel(value, path) {
    const parsed = expectString(value, path).trim();
    if (parsed.length < 1 || parsed.length > 80)
        throw new Error(`${path} must contain 1-80 characters`);
    return parsed;
}
function nullableDate(value, path) {
    return value === null ? null : parseStudentBillingDate(value, path);
}
function nullableMonth(value, path) {
    return value === null ? null : parseStudentBillingServiceMonth(value, path);
}
export function parseStudentBillingLessonKind(value, path = "lessonKind") {
    return enumValue(value, STUDENT_BILLING_LESSON_KINDS, path);
}
export function parsePutStudentBillingLessonFeeRequest(value, path = "lessonFeeRequest") {
    const record = expectRecord(value, path);
    exact(record, ["effectiveFrom", "effectiveTo", "amount", "currency", "expectedVersion"], path);
    const effectiveFrom = parseStudentBillingDate(record.effectiveFrom, `${path}.effectiveFrom`);
    const effectiveTo = nullableDate(record.effectiveTo, `${path}.effectiveTo`);
    if (effectiveTo !== null && effectiveTo < effectiveFrom)
        throw new Error(`${path}.effectiveTo is before effectiveFrom`);
    return {
        effectiveFrom,
        effectiveTo,
        amount: nonNegativeInteger(record.amount, `${path}.amount`),
        currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
        expectedVersion: record.expectedVersion === null ? null : positiveInteger(record.expectedVersion, `${path}.expectedVersion`)
    };
}
export function parseStudentBillingLessonFeeSetting(value, path = "lessonFeeSetting") {
    const record = expectRecord(value, path);
    exact(record, [
        "settingId", "tenantId", "lessonId", "lessonKindSnapshot", "amount", "currency", "effectiveFrom", "effectiveTo",
        "status", "version", "createdByPlatformUserId", "createdAt", "updatedAt"
    ], path);
    const effectiveFrom = parseStudentBillingDate(record.effectiveFrom, `${path}.effectiveFrom`);
    const effectiveTo = nullableDate(record.effectiveTo, `${path}.effectiveTo`);
    const status = enumValue(record.status, STUDENT_BILLING_FEE_STATUSES, `${path}.status`);
    if (effectiveTo !== null && effectiveTo < effectiveFrom)
        throw new Error(`${path}.effectiveTo is before effectiveFrom`);
    if (status === "ended" && effectiveTo === null)
        throw new Error(`${path}.ended requires effectiveTo`);
    return {
        settingId: parseStudentBillingIdentifier(record.settingId, `${path}.settingId`),
        tenantId: parseStudentBillingIdentifier(record.tenantId, `${path}.tenantId`),
        lessonId: parseStudentBillingIdentifier(record.lessonId, `${path}.lessonId`),
        lessonKindSnapshot: parseStudentBillingLessonKind(record.lessonKindSnapshot, `${path}.lessonKindSnapshot`),
        amount: nonNegativeInteger(record.amount, `${path}.amount`),
        currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
        effectiveFrom,
        effectiveTo,
        status,
        version: positiveInteger(record.version, `${path}.version`),
        createdByPlatformUserId: parseStudentBillingIdentifier(record.createdByPlatformUserId, `${path}.createdByPlatformUserId`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
function parseEventMonthRule(record, path) {
    const billingMonthRule = enumValue(record.billingMonthRule, STUDENT_BILLING_EVENT_BILLING_MONTH_RULES, `${path}.billingMonthRule`);
    const explicitServiceMonth = nullableMonth(record.explicitServiceMonth, `${path}.explicitServiceMonth`);
    if (billingMonthRule === "eventFirstMonth" && explicitServiceMonth !== null)
        throw new Error(`${path}.explicitServiceMonth must be null`);
    if (billingMonthRule === "explicitServiceMonth" && explicitServiceMonth === null)
        throw new Error(`${path}.explicitServiceMonth is required`);
    return { billingMonthRule, explicitServiceMonth };
}
export function parseCreateStudentBillingEventFeeItemRequest(value, path = "eventFeeItemCreateRequest") {
    const record = expectRecord(value, path);
    exact(record, ["label", "amount", "currency", "billingMonthRule", "explicitServiceMonth", "expectedVersion"], path);
    if (record.expectedVersion !== null)
        throw new Error(`${path}.expectedVersion must be null`);
    return {
        label: boundedLabel(record.label, `${path}.label`),
        amount: nonNegativeInteger(record.amount, `${path}.amount`),
        currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
        ...parseEventMonthRule(record, path),
        expectedVersion: null
    };
}
export function parseUpdateStudentBillingEventFeeItemRequest(value, path = "eventFeeItemUpdateRequest") {
    const record = expectRecord(value, path);
    exact(record, ["label", "amount", "currency", "billingMonthRule", "explicitServiceMonth", "status", "expectedVersion"], path);
    return {
        label: boundedLabel(record.label, `${path}.label`),
        amount: nonNegativeInteger(record.amount, `${path}.amount`),
        currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
        ...parseEventMonthRule(record, path),
        status: enumValue(record.status, STUDENT_BILLING_EVENT_FEE_ITEM_STATUSES, `${path}.status`),
        expectedVersion: positiveInteger(record.expectedVersion, `${path}.expectedVersion`)
    };
}
export function parseStudentBillingEventFeeItem(value, path = "eventFeeItem") {
    const record = expectRecord(value, path);
    exact(record, [
        "feeItemId", "label", "amount", "currency", "billingMonthRule", "explicitServiceMonth", "status", "version", "createdAt", "updatedAt"
    ], path);
    return {
        feeItemId: parseStudentBillingIdentifier(record.feeItemId, `${path}.feeItemId`),
        label: boundedLabel(record.label, `${path}.label`),
        amount: nonNegativeInteger(record.amount, `${path}.amount`),
        currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
        ...parseEventMonthRule(record, path),
        status: enumValue(record.status, STUDENT_BILLING_EVENT_FEE_ITEM_STATUSES, `${path}.status`),
        version: positiveInteger(record.version, `${path}.version`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function resolveStudentBillingEventServiceMonth(input) {
    if (input.billingMonthRule === "explicitServiceMonth") {
        if (input.explicitServiceMonth === null)
            throw new Error("explicitServiceMonth is required");
        return parseStudentBillingServiceMonth(input.explicitServiceMonth, "explicitServiceMonth");
    }
    if (input.explicitServiceMonth !== null)
        throw new Error("eventFirstMonth cannot contain explicitServiceMonth");
    const date = new Date(input.eventStartAt);
    if (!Number.isFinite(date.getTime()))
        throw new Error("eventStartAt is invalid");
    const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit" }).formatToParts(date);
    const year = parts.find((part) => part.type === "year")?.value;
    const month = parts.find((part) => part.type === "month")?.value;
    if (!year || !month)
        throw new Error("eventStartAt is invalid");
    return `${year}-${month}`;
}
